import {
  ListingColumnKey,
  ListingColumnPreferenceRow,
  DEFAULT_ACTIONS_COLUMN_WIDTH,
  ACTIONS_COLUMN_MIN_WIDTH,
  minWidthForListingColumnKey,
} from "../../constants/listingColumnKeys";

export interface ColumnWithListingKey {
  listingColumnKey: string;
  width?: number;
  [key: string]: unknown;
}

export interface ApplyListingColumnPreferencesOptions {
  /** Override default width used when clamping actions column (defaults: admin/doctor). */
  actionsDefaultWidth?: number;
  /** Override minimum width for actions column (defaults: admin/doctor). */
  actionsMinWidth?: number;
  /** Keys that must appear first, in this order (subset may be missing if not visible). */
  lockedLeadingKeys?: readonly string[];
}

function reorderWithLockedLeading<T extends ColumnWithListingKey>(
  columns: T[],
  locked: readonly string[]
): T[] {
  if (!locked.length) return columns;
  const byKey = new Map(columns.map((c) => [c.listingColumnKey, c]));
  const front: T[] = [];
  for (const k of locked) {
    const c = byKey.get(k);
    if (c) front.push(c);
  }
  const lockedSet = new Set(locked);
  const rest = columns.filter((c) => !lockedSet.has(c.listingColumnKey));
  return [...front, ...rest];
}

/**
 * Orders and filters Ant Design table columns using server-persisted preferences.
 * If preferences are empty, uses fallbackOrder (typically API eligible_column_keys order).
 */
export function applyListingColumnPreferences<T extends ColumnWithListingKey>(
  defsByKey: Record<string, T | undefined>,
  preferences: ListingColumnPreferenceRow[] | null | undefined,
  fallbackOrder: string[],
  options?: ApplyListingColumnPreferencesOptions
): T[] {
  const actionsDefault = options?.actionsDefaultWidth ?? DEFAULT_ACTIONS_COLUMN_WIDTH;
  const actionsMin = options?.actionsMinWidth ?? ACTIONS_COLUMN_MIN_WIDTH;
  const clampActionsWidth = (w: number | undefined): number =>
    Math.max(w ?? actionsDefault, actionsMin);

  const locked = options?.lockedLeadingKeys;
  const lockedSet = locked?.length ? new Set(locked) : null;

  if (!preferences?.length) {
    const base = fallbackOrder
      .map((k) => defsByKey[k])
      .filter((c): c is T => Boolean(c))
      .map((c) =>
        c.listingColumnKey === ListingColumnKey.ACTIONS
          ? ({ ...c, width: clampActionsWidth(c.width as number | undefined) } as T)
          : c
      );
    return locked?.length ? reorderWithLockedLeading(base, locked) : base;
  }

  const out: T[] = [];
  const addedKeys = new Set<string>();

  for (const p of preferences) {
    const alwaysShow = Boolean(lockedSet?.has(p.key));
    if (!p.visible && !alwaysShow) continue;
    const def = defsByKey[p.key];
    if (!def) continue;
    const minForColumn = minWidthForListingColumnKey(p.key, actionsMin);
    let width =
      typeof p.width === "number" && p.width >= minForColumn
        ? p.width
        : (def.width as number | undefined);
    if (p.key === ListingColumnKey.ACTIONS) {
      width = clampActionsWidth(width);
    }
    out.push({ ...def, width } as T);
    addedKeys.add(p.key);
  }

  if (lockedSet && locked?.length) {
    for (const k of locked) {
      if (addedKeys.has(k)) continue;
      const def = defsByKey[k];
      if (!def) continue;
      let width = def.width as number | undefined;
      if (k === ListingColumnKey.ACTIONS) {
        width = clampActionsWidth(width);
      }
      out.push({ ...def, width } as T);
      addedKeys.add(k);
    }
  }

  return locked?.length ? reorderWithLockedLeading(out, locked) : out;
}
